import React, { useState } from "react";
import { branchesReleaseNamesSelector } from "src/stores/selectors/branchDeployerSelector";
import useAppStore from "src/stores/appStore";
import StepUseCase from "./components/StepUseCase";
import StepConfiguration from "./components/StepConfiguration";
import StepReview from "./components/StepReview";
import {
  WIZARD_MODES,
  WIZARD_INITIAL_DATA,
  USE_CASES,
  STEP_LABELS,
  LATEST_TAG,
  NLU_MODES,
  FIELD_IDS,
  BUTTON_TEXTS,
  getServicesForUseCase
} from "./DeployWizard.consts";
import { BRANCH_DEPLOYER_STORAGE_KEY } from "../BranchDeployer.consts";
import {
  validateReleaseName,
  validateShaField,
  validateAssistantId,
  validateConfigId,
  validateMemory,
  getInitialFormData,
  getInitialUseCase,
  buildPayload,
  getInitialLatestToggles,
  getInitialErrors,
  getInitialStep,
  normalizeShaInput,
} from "./DeployWizard.utils";

const useDeployWizard = ({ mode, initialData, onSubmit, onClose }) => {
  const branchesReleaseNames = useAppStore(branchesReleaseNamesSelector) || [];
  const isEdit = mode === WIZARD_MODES.EDIT;
  const isReactivate = mode === WIZARD_MODES.REACTIVATE;
  const isView = mode === WIZARD_MODES.VIEW;

  const initialUseCase = getInitialUseCase(mode, initialData);
  const hasRestoredData = !isEdit && !isReactivate && !!initialUseCase;

  const [initialErrors] = useState(() => getInitialErrors({
    mode,
    formData: getInitialFormData(mode, initialData),
    useCaseId: initialUseCase,
    existingNames: branchesReleaseNames,
    hasRestoredData
  }));

  const [activeStep, setActiveStep] = useState(() => getInitialStep(mode, hasRestoredData, Object.keys(initialErrors).length > 0));
  const [selectedUseCase, setSelectedUseCase] = useState(initialUseCase);
  const [formData, setFormData] = useState(() => getInitialFormData(mode, initialData));
  const [latestToggles, setLatestToggles] = useState(() => {
    if (!hasRestoredData && !isEdit && !isReactivate && !isView) return {};
    return getInitialLatestToggles(getInitialFormData(mode, initialData), initialUseCase);
  });
  const [errors, setErrors] = useState(initialErrors);
  const [isDeploying, setIsDeploying] = useState(false);

  const handleSelectUseCase = (useCaseId) => {
    setSelectedUseCase(useCaseId);
    setFormData((prev) => ({
      ...WIZARD_INITIAL_DATA,
      releaseName: prev.releaseName,
      ttlHours: prev.ttlHours
    }));
    setLatestToggles({});
    setErrors(formData[FIELD_IDS.RELEASE_NAME]
      ? { [FIELD_IDS.RELEASE_NAME]: validateReleaseName(formData[FIELD_IDS.RELEASE_NAME], branchesReleaseNames, isEdit) }
      : {});
  };

  const handleFieldChange = (fieldId, value) => {
    let formatted = value;
    if (fieldId === FIELD_IDS.RELEASE_NAME) {
      formatted = value.replace(/ /g, "-");
    }

    const newFormData = { ...formData, [fieldId]: formatted };
    setFormData(newFormData);

    let fieldError = "";
    if (fieldId === FIELD_IDS.RELEASE_NAME) {
      fieldError = validateReleaseName(formatted, branchesReleaseNames, isEdit);
    } else if (fieldId === FIELD_IDS.ASSISTANT_ID) {
      fieldError = validateAssistantId(formatted);
    } else if (fieldId === FIELD_IDS.CONFIG_ID) {
      fieldError = validateConfigId(formatted);
    } else if (fieldId === FIELD_IDS.MEMORY) {
      fieldError = validateMemory(formatted);
    }

    setErrors((prev) => ({ ...prev, [fieldId]: fieldError }));
  };

  const handleServiceValueChange = (serviceId, rawValue) => {
    const value = normalizeShaInput(rawValue);
    const newFormData = { ...formData, [serviceId]: value };
    setFormData(newFormData);

    const { required, optional } = getServicesForUseCase(selectedUseCase);
    const service = [...required, ...optional].find((s) => s.id === serviceId);
    const allowLatest = service?.showLatestToggle !== false;
    const error = validateShaField(value, allowLatest);
    setErrors((prev) => ({ ...prev, [serviceId]: error }));
  };

  const handleLatestToggle = (serviceId, checked) => {
    setLatestToggles((prev) => ({ ...prev, [serviceId]: checked }));

    if (checked) {
      setFormData((prev) => ({ ...prev, [serviceId]: LATEST_TAG }));
      setErrors((prev) => ({ ...prev, [serviceId]: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [serviceId]: "" }));
    }
  };

  const handleDeploy = async () => {
    if (isView) {
      onClose();
      return;
    }
    setIsDeploying(true);
    try {
      await onSubmit(buildPayload(formData, selectedUseCase), mode);
      if (mode === WIZARD_MODES.CREATE) {
        localStorage.removeItem(BRANCH_DEPLOYER_STORAGE_KEY);
      }
    } catch {
      if (mode === WIZARD_MODES.CREATE) {
        localStorage.setItem(
          BRANCH_DEPLOYER_STORAGE_KEY,
          JSON.stringify({ ...formData, [FIELD_IDS.USE_CASE]: selectedUseCase })
        );
      }
    } finally {
      setIsDeploying(false);
    }
  };

  const getRelevantFieldIds = () => {
    const { required, optional } = getServicesForUseCase(selectedUseCase);
    const ids = new Set([
      FIELD_IDS.RELEASE_NAME,
      FIELD_IDS.MEMORY,
      ...required.map((s) => s.id),
      ...optional.map((s) => s.id)
    ]);
    const useCase = USE_CASES[selectedUseCase];
    const effectiveNluMode = formData[FIELD_IDS.NLU_MODE] || useCase?.nluMode;
    if (effectiveNluMode === NLU_MODES.DEDICATED) {
      ids.add(FIELD_IDS.ASSISTANT_ID);
      ids.add(FIELD_IDS.CONFIG_ID);
    }
    return ids;
  };

  const getFirstRelevantError = () => {
    const relevantIds = getRelevantFieldIds();
    return Object.entries(errors).find(([key, value]) => relevantIds.has(key) && value);
  };

  const isStep2Valid = () => {
    if (!formData[FIELD_IDS.RELEASE_NAME]) return false;
    if (getFirstRelevantError()) return false;

    const { required } = getServicesForUseCase(selectedUseCase);
    const allRequiredFilled = required.every((service) => {
      const value = formData[service.id];
      return value && value !== "";
    });
    if (!allRequiredFilled) return false;

    const useCase = USE_CASES[selectedUseCase];
    const effectiveNluMode = formData[FIELD_IDS.NLU_MODE] || useCase?.nluMode;
    if (effectiveNluMode === NLU_MODES.DEDICATED) {
      if (!formData[FIELD_IDS.ASSISTANT_ID] || !formData[FIELD_IDS.CONFIG_ID]) return false;
    }

    return true;
  };

  const getConfigDisabledReason = () => {
    if (!formData[FIELD_IDS.RELEASE_NAME]) return "Enter a release name";

    if (getFirstRelevantError()) return "Fix validation errors to continue";

    const { required } = getServicesForUseCase(selectedUseCase);
    const missingRequired = required.find((service) => {
      const value = formData[service.id];
      return !value || value === "";
    });
    if (missingRequired) return `Fill all required fields (${missingRequired.label})`;

    const useCase = USE_CASES[selectedUseCase];
    const effectiveNluMode = formData[FIELD_IDS.NLU_MODE] || useCase?.nluMode;
    if (effectiveNluMode === NLU_MODES.DEDICATED) {
      if (!formData[FIELD_IDS.ASSISTANT_ID]) return "Enter an Assistant ID";
      if (!formData[FIELD_IDS.CONFIG_ID]) return "Enter a Config ID";
    }

    return "";
  };

  const getPrimaryButtonText = () => {
    if (isView) return BUTTON_TEXTS.CLOSE;
    if (activeStep < 2) return BUTTON_TEXTS.NEXT;
    if (isEdit) return BUTTON_TEXTS.SAVE;
    if (isReactivate) return BUTTON_TEXTS.REACTIVATE;
    return BUTTON_TEXTS.DEPLOY;
  };

  const steps = [
    {
      label: STEP_LABELS.USE_CASE + (selectedUseCase ? ` (${USE_CASES[selectedUseCase].label})` : ""),
      component: (
        <StepUseCase
          selectedUseCase={selectedUseCase}
          onSelectUseCase={handleSelectUseCase}
        />
      ),
      nextDisabled: !selectedUseCase,
      primaryButtonTooltip: !selectedUseCase ? "Select a use case to continue" : "",
      primaryButtonText: BUTTON_TEXTS.NEXT,
      secondaryButtonText: BUTTON_TEXTS.CANCEL
    },
    {
      label: STEP_LABELS.CONFIGURATION,
      component: (
        <StepConfiguration
          mode={mode}
          selectedUseCase={selectedUseCase}
          formData={formData}
          latestToggles={latestToggles}
          errors={errors}
          onFieldChange={handleFieldChange}
          onValueChange={handleServiceValueChange}
          onLatestToggle={handleLatestToggle}
        />
      ),
      nextDisabled: !isStep2Valid(),
      primaryButtonTooltip: getConfigDisabledReason(),
      primaryButtonText: BUTTON_TEXTS.NEXT,
      secondaryButtonText: isEdit ? null : BUTTON_TEXTS.BACK,
    },
    {
      label: isView ? STEP_LABELS.DETAILS : STEP_LABELS.REVIEW,
      component: (
        <StepReview
          selectedUseCase={selectedUseCase}
          formData={formData}
          latestToggles={latestToggles}
          urls={isView ? initialData : null}
          isView={isView}
        />
      ),
      nextDisabled: isDeploying,
      primaryButtonText: getPrimaryButtonText(),
      primaryButtonLoading: isView ? false : isDeploying,
      secondaryButtonText: isView ? null : BUTTON_TEXTS.BACK,
    }
  ];

  return {
    steps,
    activeStep,
    setActiveStep,
    isDeploying,
    handleDeploy,
    exitWizard: onClose
  };
};

export default useDeployWizard;
